import type { WidgetBranding, WidgetPalette } from './types'

type ThemeColorOptions = {
  accent: string
  background: string
  surface: string
  secondary?: string
  palette?: WidgetPalette
}

const FALLBACK_ACCENT = '#6366f1'
const FALLBACK_BACKGROUND = '#0f172a'
const FALLBACK_SURFACE = '#111c36'

export function normalizeHex(value: string | undefined, fallback: string): string {
  if (!value) return fallback
  const trimmed = value.trim()
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) {
    if (trimmed.length === 4) {
      return (
        '#' +
        trimmed
          .slice(1)
          .split('')
          .map((ch) => ch + ch)
          .join('')
      ).toLowerCase()
    }
    return trimmed.toLowerCase()
  }
  return fallback
}

export function normalizeCssColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback
  const trimmed = value.trim()
  if (!trimmed) return fallback
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) {
    return normalizeHex(trimmed, fallback)
  }
  return trimmed
}

function blendWithWhite(color: string, intensity: number) {
  const amount = Math.min(1, Math.max(0, intensity))
  const hex = normalizeHex(color, FALLBACK_ACCENT).slice(1)
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  const mix = (component: number) =>
    Math.round(component + (255 - component) * amount)
  return `#${[mix(r), mix(g), mix(b)]
    .map((component) => component.toString(16).padStart(2, '0'))
    .join('')}`
}

function blendWithBlack(color: string, intensity: number) {
  const amount = Math.min(1, Math.max(0, intensity))
  const hex = normalizeHex(color, FALLBACK_ACCENT).slice(1)
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  const mix = (component: number) =>
    Math.round(component * (1 - amount))
  return `#${[mix(r), mix(g), mix(b)]
    .map((component) => component.toString(16).padStart(2, '0'))
    .join('')}`
}

function hexToRgb(hex: string) {
  const normalized = normalizeHex(hex, '')
  if (!normalized) return null
  const value = normalized.slice(1)
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)
    ? null
    : { r, g, b }
}

function rgbToHex(rgb: { r: number; g: number; b: number }) {
  const clamp = (component: number) =>
    Math.min(255, Math.max(0, Math.round(component)))
  return `#${[clamp(rgb.r), clamp(rgb.g), clamp(rgb.b)]
    .map((component) => component.toString(16).padStart(2, '0'))
    .join('')}`
}

function mixHexColors(colorA: string, colorB: string, weight: number) {
  const rgbA = hexToRgb(colorA)
  const rgbB = hexToRgb(colorB)
  if (!rgbA || !rgbB) return colorA

  const w = Math.min(1, Math.max(0, weight))
  const mix = (a: number, b: number) => a * w + b * (1 - w)
  return rgbToHex({
    r: mix(rgbA.r, rgbB.r),
    g: mix(rgbA.g, rgbB.g),
    b: mix(rgbA.b, rgbB.b),
  })
}

export function toRgba(hex: string, alpha: number) {
  const rgb = hexToRgb(hex)
  if (!rgb) return `rgba(0, 0, 0, ${Math.min(Math.max(alpha, 0), 1)})`
  const clampedAlpha = Math.min(1, Math.max(0, Number(alpha)))
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clampedAlpha})`
}

function getLuminance(hex: string) {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0
  const transform = (component: number) => {
    const value = component / 255
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4)
  }
  const r = transform(rgb.r)
  const g = transform(rgb.g)
  const b = transform(rgb.b)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function getContrastingTextColor(hex: string) {
  const luminance = getLuminance(hex)
  return luminance > 0.55 ? '#111827' : '#f8fafc'
}

export type ThemeVariableMap = Record<string, string>

export function computeThemeVariables({
  accent,
  background,
  surface,
  secondary,
  palette,
}: ThemeColorOptions): ThemeVariableMap {
  const primaryColor = normalizeHex(palette?.primaryColor, accent || FALLBACK_ACCENT)
  const secondaryColor = normalizeHex(
    palette?.secondaryColor ?? secondary,
    primaryColor
  )
  const backgroundColor = normalizeHex(
    palette?.backgroundAlt ?? palette?.backgroundColor ?? background,
    FALLBACK_BACKGROUND
  )
  const surfaceColor = normalizeHex(
    palette?.surfaceColor ?? surface,
    FALLBACK_SURFACE
  )

  const primaryLight = normalizeHex(
    palette?.primaryLight,
    blendWithWhite(primaryColor, 0.2)
  )
  const primaryHover = normalizeHex(
    palette?.primaryHover,
    blendWithBlack(primaryColor, 0.1)
  )
  const secondaryLight = normalizeHex(
    palette?.secondaryLight,
    blendWithWhite(secondaryColor, 0.35)
  )

  const accentSoft = palette?.secondaryColor
    ? secondaryColor
    : blendWithWhite(primaryColor, 0.35)
  const accentSofter = normalizeHex(
    palette?.surfaceHover,
    blendWithWhite(primaryColor, 0.75)
  )

  const surfaceHover = palette?.surfaceHover
    ? normalizeCssColor(palette.surfaceHover, toRgba(surfaceColor, 0.82))
    : toRgba(surfaceColor, 0.82)
  const surfaceTransparent = palette?.surfaceColor
    ? normalizeCssColor(
        palette.surfaceColor,
        toRgba(normalizeHex(palette.surfaceColor, surfaceColor), 0.72)
      )
    : toRgba(surfaceColor, 0.72)

  const border = palette?.borderColor
    ? normalizeCssColor(palette.borderColor, toRgba(surfaceColor, 0.28))
    : toRgba(surfaceColor, 0.28)
  const borderLight = palette?.borderLight
    ? normalizeCssColor(palette.borderLight, toRgba(surfaceColor, 0.18))
    : toRgba(surfaceColor, 0.18)
  const shadow = palette?.shadowColor
    ? normalizeCssColor(palette.shadowColor, toRgba(surfaceColor, 0.45))
    : toRgba(surfaceColor, 0.45)
  const shadowLight = palette?.shadowLight
    ? normalizeCssColor(palette.shadowLight, toRgba(surfaceColor, 0.25))
    : toRgba(surfaceColor, 0.25)

  const textPrimary = palette?.textPrimary
    ? normalizeCssColor(palette.textPrimary, getContrastingTextColor(surfaceColor))
    : getContrastingTextColor(surfaceColor)
  const textSecondary = palette?.textSecondary
    ? normalizeCssColor(palette.textSecondary, toRgba(textPrimary, 0.85))
    : toRgba(textPrimary, 0.85)
  const textMuted = palette?.textMuted
    ? normalizeCssColor(palette.textMuted, toRgba(textPrimary, 0.6))
    : toRgba(textPrimary, 0.6)

  const bgPrimary = normalizeHex(
    palette?.backgroundColor ?? backgroundColor,
    backgroundColor
  )
  const bgSecondary = palette?.backgroundAlt
    ? normalizeHex(palette.backgroundAlt, backgroundColor)
    : mixHexColors(bgPrimary, surfaceColor, 0.55)

  const gradientStart = normalizeCssColor(
    palette?.gradientStart,
    primaryColor
  )
  const gradientEnd = normalizeCssColor(
    palette?.gradientEnd,
    primaryLight
  )

  // Message-specific colors with smart defaults
  const userMessageBg = normalizeHex(
    palette?.userMessageBg,
    primaryColor
  )
  const userMessageText = palette?.userMessageText
    ? normalizeCssColor(palette.userMessageText, getContrastingTextColor(userMessageBg))
    : getContrastingTextColor(userMessageBg)

  const agentMessageBg = palette?.agentMessageBg
    ? normalizeCssColor(palette.agentMessageBg, surfaceTransparent)
    : surfaceTransparent
  const agentMessageText = palette?.agentMessageText
    ? normalizeCssColor(palette.agentMessageText, textPrimary)
    : textPrimary

  // Header text colors
  const headerTitleColor = palette?.headerTitleColor
    ? normalizeCssColor(palette.headerTitleColor, textPrimary)
    : textPrimary
  const headerSubtitleColor = palette?.headerSubtitleColor
    ? normalizeCssColor(palette.headerSubtitleColor, textSecondary)
    : textSecondary

  // Interactive element colors (launcher and send button)
  const launcherBg = normalizeHex(
    palette?.launcherBg,
    primaryColor
  )
  const launcherIcon = palette?.launcherIcon
    ? normalizeCssColor(palette.launcherIcon, getContrastingTextColor(launcherBg))
    : getContrastingTextColor(launcherBg)

  const sendButtonBg = normalizeHex(
    palette?.sendButtonBg,
    primaryColor
  )
  const sendButtonIcon = palette?.sendButtonIcon
    ? normalizeCssColor(palette.sendButtonIcon, getContrastingTextColor(sendButtonBg))
    : getContrastingTextColor(sendButtonBg)

  const linkColor = palette?.linkColor
    ? normalizeCssColor(palette.linkColor, primaryLight)
    : primaryLight

  const linkHoverColor = palette?.linkHoverColor
    ? normalizeCssColor(palette.linkHoverColor, primaryColor)
    : primaryColor

  // User message shadow (derived from message bg, not primary!)
  const userMessageShadow = palette?.userMessageShadow
    ? normalizeCssColor(palette.userMessageShadow, toRgba(userMessageBg, 0.35))
    : toRgba(userMessageBg, 0.35)

  return {
    '--chat-accent': primaryColor,
    '--chat-accent-soft': accentSoft,
    '--chat-accent-softer': accentSofter,
    '--chat-primary': primaryColor,
    '--chat-primary-light': primaryLight,
    '--chat-primary-hover': primaryHover,
    '--chat-secondary': secondaryColor,
    '--chat-secondary-light': secondaryLight,
    '--chat-background': bgPrimary,
    '--chat-background-alt': bgSecondary,
    '--chat-surface': surfaceColor,
    '--chat-bg-primary': bgPrimary,
    '--chat-bg-secondary': bgSecondary,
    '--chat-bg-surface': surfaceTransparent,
    '--chat-bg-surface-hover': surfaceHover,
    '--chat-border': border,
    '--chat-border-light': borderLight,
    '--chat-shadow': shadow,
    '--chat-shadow-light': shadowLight,
    '--chat-text-primary': textPrimary,
    '--chat-text-secondary': textSecondary,
    '--chat-text-muted': textMuted,
    '--chat-gradient-start': gradientStart,
    '--chat-gradient-end': gradientEnd,
    '--chat-user-message-bg': userMessageBg,
    '--chat-user-message-text': userMessageText,
    '--chat-user-message-shadow': userMessageShadow,
    '--chat-agent-message-bg': agentMessageBg,
    '--chat-agent-message-text': agentMessageText,
    '--chat-header-title': headerTitleColor,
    '--chat-header-subtitle': headerSubtitleColor,
    '--chat-launcher-bg': launcherBg,
    '--chat-launcher-icon': launcherIcon,
    '--chat-send-button-bg': sendButtonBg,
    '--chat-send-button-icon': sendButtonIcon,
    '--chat-link-color': linkColor,
    '--chat-link-hover-color': linkHoverColor,
  }
}

export function buildThemeStyles(options: ThemeColorOptions) {
  const variables = computeThemeVariables(options)
  return Object.entries(variables)
    .map(([key, value]) => `${key}:${value};`)
    .join('\n')
}

export function deriveThemeFromBranding(
  branding?: WidgetBranding | null,
  fallbackAccent = FALLBACK_ACCENT,
  fallbackBackground = FALLBACK_BACKGROUND,
  fallbackSurface = FALLBACK_SURFACE
): ThemeVariableMap {
  if (!branding) {
    return computeThemeVariables({
      accent: fallbackAccent,
      background: fallbackBackground,
      surface: fallbackSurface,
    })
  }

  return computeThemeVariables({
    accent: branding.primaryColor || fallbackAccent,
    secondary: branding.secondaryColor || branding.primaryColor || fallbackAccent,
    background: branding.backgroundColor || fallbackBackground,
    surface: branding.surfaceColor || fallbackSurface,
    palette: branding.palette,
  })
}

export function formatBackgroundImage(image?: string | null) {
  const trimmed = image?.trim()
  if (!trimmed) {
    return 'background-image: none'
  }
  return `background-image: url('${trimmed.replace(/'/g, "\\'")}')`
}
